import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Play, LayoutGrid, List } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SegmentedTabs } from "@akuna/ui";
import { MobileFlowboardFilterChips } from "./MobileFlowboardFilterChips";
import { MobileFlowboardFilterSheet, type FilterOption } from "./MobileFlowboardFilterSheet";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";

type Props = {
  view: "kanban" | "list";
  setView: (v: "kanban" | "list") => void;
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;
  goToPreviousDay: () => void;
  goToNextDay: () => void;
  dateLabel: string;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  vetOptions: FilterOption[];
  selectedVets: string[];
  toggleVet: (vetId: string) => void;
  typeOptions: FilterOption[];
  selectedTypes: string[];
  toggleType: (type: string) => void;
  onlyTaxiDog: boolean;
  setOnlyTaxiDog: (v: boolean) => void;
  statuses: AppointmentStatus[];
  isColumnVisible: (name: string) => boolean;
  toggleColumn: (name: string) => void;
  columnCounts: Record<string, number>;
  getStatusColor: (name: string, statuses: AppointmentStatus[]) => string;
  activeFiltersCount: number;
  clearFilters: () => void;
  canShowQuickStart: boolean;
  clinicId: string | undefined;
  isClinicLoading: boolean;
  onCreateAppointment: () => void;
  onStartAttendance: () => void;
};

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function fromDateInputValue(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function MobileFlowboardHeader(props: Props) {
  const { t } = useTranslation("calendar");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  useMobileHeader({
    title: t("flowboardTitle"),
    search: (
      <MobileFlowboardFilterChips
        activeCount={props.activeFiltersCount}
        searchTerm={props.searchTerm}
        onOpen={() => setFiltersOpen(true)}
      />
    ),
  }, [t, props.activeFiltersCount, props.searchTerm]);

  const handleFabClick = () => {
    if (props.canShowQuickStart) {
      setActionsOpen(true);
    } else {
      props.onCreateAppointment();
    }
  };

  const fabDisabled = props.isClinicLoading || !props.clinicId;

  const fabClassName = cn(
    "fixed bottom-[calc(96px+env(safe-area-inset-bottom))] right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/25 md:hidden",
    fabDisabled && "opacity-50",
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <button
            type="button"
            aria-label={t("flowboardMobile.previousDay")}
            onClick={props.goToPreviousDay}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={t("appointmentForm.selectDate")}
            onClick={() => {
              const input = dateInputRef.current;
              if (!input) return;
              if (typeof input.showPicker === "function") {
                input.showPicker();
              } else {
                input.click();
              }
            }}
            className="relative flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-3 text-[12px] font-semibold text-foreground"
          >
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{props.dateLabel}</span>
            <input
              ref={dateInputRef}
              type="date"
              aria-hidden="true"
              tabIndex={-1}
              className="absolute h-0 w-0 opacity-0"
              value={toDateInputValue(props.selectedDate)}
              onChange={(e) => {
                if (e.target.value) {
                  props.setSelectedDate(fromDateInputValue(e.target.value));
                }
              }}
            />
          </button>
          <button
            type="button"
            aria-label={t("flowboardMobile.nextDay")}
            onClick={props.goToNextDay}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <SegmentedTabs
          className="!gap-0 shrink-0 [&_.akuna-seg-tabs\_\_list]:h-9 [&_.akuna-seg-tabs\_\_list]:items-stretch [&_.akuna-seg-tabs\_\_list]:!border-border [&_.akuna-seg-tabs\_\_trigger]:h-full [&_.akuna-seg-tabs\_\_trigger]:w-9 [&_.akuna-seg-tabs\_\_trigger]:p-0"
          variant="outline"
          value={props.view}
          onValueChange={(value) => props.setView(value as "kanban" | "list")}
          items={[
            {
              value: "kanban",
              icon: LayoutGrid,
              label: <span className="sr-only">{t("flowboardMobile.viewKanban")}</span>,
            },
            {
              value: "list",
              icon: List,
              label: <span className="sr-only">{t("flowboardMobile.viewList")}</span>,
            },
          ]}
        />
      </div>

      <MobileFlowboardFilterSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchTerm={props.searchTerm}
        setSearchTerm={props.setSearchTerm}
        vetOptions={props.vetOptions}
        selectedVets={props.selectedVets}
        toggleVet={props.toggleVet}
        typeOptions={props.typeOptions}
        selectedTypes={props.selectedTypes}
        toggleType={props.toggleType}
        onlyTaxiDog={props.onlyTaxiDog}
        setOnlyTaxiDog={props.setOnlyTaxiDog}
        isKanbanView={props.view === "kanban"}
        statuses={props.statuses}
        isColumnVisible={props.isColumnVisible}
        toggleColumn={props.toggleColumn}
        columnCounts={props.columnCounts}
        getStatusColor={props.getStatusColor}
        onClear={props.clearFilters}
      />

      <button
        type="button"
        aria-label={
          props.canShowQuickStart
            ? t("flowboardMobile.actionsMenuLabel")
            : t("flowboardMobile.newAppointment")
        }
        disabled={fabDisabled}
        onClick={handleFabClick}
        className={fabClassName}
      >
        <Plus className="h-5 w-5" />
      </button>

      {props.canShowQuickStart ? (
        <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
          <SheetContent side="bottom" className="rounded-t-3xl">
            <SheetHeader className="text-left">
              <SheetTitle>{t("flowboardMobile.actionsMenuLabel")}</SheetTitle>
            </SheetHeader>
            <div className="space-y-2 py-4">
              <button
                type="button"
                onClick={() => {
                  setActionsOpen(false);
                  props.onStartAttendance();
                }}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-[15px] font-semibold text-foreground"
              >
                <Play className="h-4 w-4 text-primary" />
                {t("flowboardMobile.startAttendance")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setActionsOpen(false);
                  props.onCreateAppointment();
                }}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-[15px] font-semibold text-foreground"
              >
                <Plus className="h-4 w-4 text-primary" />
                {t("flowboardMobile.newAppointment")}
              </button>
            </div>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
